// @LTG-ID: contact-multi-step-form
// @STATUS: BUILT
// @HERMETIC: true (frontend-only state machine; POST /api/contact is the only
//                  network call this orchestrator makes)
// @DOCS: source `Contact.html` lines 1195-1281 (v4), ORCH-0087 §scope
// @CHANGED: 2026-05 session_006 (CHG-0083-M-A2)
// @OWNER: web
//
// CHG-0083-M-A2 — Multi-step form container + state machine.
//
// View state machine (was just 0..5 in M-A1; expanded in M-A2):
//   0 .. 4              5 step indices (Name, Business, Email, Category, Details)
//   "sending"           between Submit-click and 201/error response
//   "confirmation"      after 201 success
//   "error"             after 429 / 503 / network error
//
// Submit flow (per ORCH-0087 point 2):
//   1. Generate `kgob_idempotency_key = "ltg-contact-" + uuidv4()` (or reuse
//      existing key on retry — same key → backend dedupe).
//   2. POST /api/contact with payload built by submitContact.js (extras folded
//      into attribution per ADR-032 §3).
//   3. On 201 → stash record_id, push `?ref=&category=` into URL, transition
//      to <Confirmation>.
//   4. On 429/503/network → transition to <SendingError>. Retry reuses the
//      same key.
//   5. Minimum visible duration on <Sending> = 800ms (ORCH-0087 point 3).
//   6. Draft cleared from localStorage on confirmation success.
//
// URL ?ref= rehydration (per ORCH-0087 point 6 + A.3 #4):
//   Backend GET /api/contact/{id} does NOT exist (verified 2026-05-25). Per
//   ORCH-0087 hard rule, NOT inventing a new endpoint this turn. Fallback:
//   v4 source's pure-frontend rehydration (lines 1210-1222) — if URL carries
//   both `?ref=<key>&category=<cat>` AND localStorage has matching draft
//   data, skip to <Confirmation>. Otherwise ignore the params and start
//   fresh at step 1.

import { useCallback, useEffect, useRef, useState } from "react";
import StepProgress from "./StepProgress";
import StepName from "./StepName";
import StepBusiness from "./StepBusiness";
import StepEmail from "./StepEmail";
import StepCategory from "./StepCategory";
import StepDetails from "./StepDetails";
import Sending from "./Sending";
import Confirmation from "./Confirmation";
import SendingError from "./SendingError";
import { submitContact, SubmitError } from "./submitContact";
import {
    generateIdempotencyKey,
    validateIdempotencyKey,
} from "./idempotency";

const STORAGE_KEY = "ltg_contact_v1";
const TOTAL_STEPS = 5;
const SENDING_MIN_VISIBLE_MS = 800; // ORCH-0087 point 3

const INITIAL_DATA = {
    firstName: "",
    lastName: "",
    middleName: "",
    businessName: "",
    state: "",
    email: "",
    emailVerified: false,
    email_verified_at: null,
    category: "",
    message: "",
    phone: "",
    kgob_idempotency_key: null,
    record_id: null,
    // CHG-0112 A2 · Honeypot — hidden `website` field. Real users cannot
    // fill it (visually hidden, tabIndex -1, aria-hidden). Bots that
    // auto-fill every input in a form will fill it. Backend responds
    // 202 Accepted (no persist, no email) on non-empty submission.
    website: "",
};

const loadDraft = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    } catch (e) {
        return null;
    }
};
const saveDraft = (d) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
    } catch (e) {
        /* localStorage may be disabled — silent */
    }
};
const clearDraft = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        /* silent */
    }
};

// CHG-0083-M-A2-fix (ORCH-0088) — defensive URL push helper.
//
// Why this is a helper instead of inline:
//   1. Single point of truth for the push contract (encodeURIComponent on
//      both values, omit `&category=` when absent, warn-and-skip if the
//      reference id is missing).
//   2. Makes the call site at Confirmation entry a one-liner so the read
//      order ("push URL, then mount Confirmation") is obvious.
//
// Contract per ORCH-0088 point 3:
//   - encodeURIComponent on BOTH `referenceId` and `category`.
//   - If `referenceId` is falsy → console.warn + skip push (Confirmation
//     still mounts; we just don't have a shareable URL state).
//   - If `category` is falsy → omit the `&category=` param entirely.
//   - Uses `history.replaceState(null, "", …)` (not `pushState`) so the
//     user cannot "back" out of Confirmation into a dead Sending state.
function pushConfirmationUrl(referenceId, category) {
    if (!referenceId) {
        // eslint-disable-next-line no-console
        console.warn(
            "[contact] pushConfirmationUrl skipped: referenceId missing"
        );
        return;
    }
    try {
        let qs = `?ref=${encodeURIComponent(referenceId)}`;
        if (category) {
            qs += `&category=${encodeURIComponent(category)}`;
        }
        const newUrl = `${window.location.pathname}${qs}`;
        window.history.replaceState(null, "", newUrl);
    } catch (e) {
        // URL push failure must not abort confirmation flow.
    }
}

export const MultiStepForm = () => {
    // view ∈ number (0..4) | "sending" | "confirmation" | "error"
    const [view, setView] = useState(0);
    const [data, setData] = useState(() => loadDraft() || INITIAL_DATA);
    const [errorState, setErrorState] = useState(null); // { kind, retryAfter }
    const dataRef = useRef(data);

    // CHG-0083-M-A2-fix-2 (ORCH-0089) — defensive URL re-assert.
    //
    // Background: ORCH-0089 instrumented run found that pushConfirmationUrl
    // fires exactly once (from performSubmit on the 201 branch) and the URL
    // is correctly stamped with `?ref=…&category=…`. Static grep across
    // /app/frontend/src confirms only three call sites of
    // window.history.replaceState/pushState exist: pushConfirmationUrl
    // (here, intentional), restart() (only on user-initiated reset), and
    // writeSectorToUrl (only fires from SectorSelector on the Home page,
    // never from any contact-tree code). The bug DOES NOT REPRODUCE in
    // this environment. The tester observed `?category=snapshot` (no ref)
    // on the live preview — a symptom this defense covers.
    //
    // Defense contract:
    //   - On every render where view === "confirmation" AND we have a
    //     reference id in state, verify the URL carries the expected
    //     `?ref=<id>` value. If not, re-push.
    //   - Idempotent: the no-op case (URL already correct) costs only a
    //     URLSearchParams read.
    //   - Covers the cold-reload path (URL already has params, no-op),
    //     the explicit submit path (helper already pushed, no-op), and
    //     any hypothetical mid-render URL strip we cannot reproduce.
    useEffect(() => {
        if (view !== "confirmation") return;
        if (!data.kgob_idempotency_key) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("ref") !== data.kgob_idempotency_key) {
            pushConfirmationUrl(
                data.kgob_idempotency_key,
                data.category
            );
        }
    }, [view, data.kgob_idempotency_key, data.category]);

    // Keep a ref of latest data for retry handler.
    useEffect(() => {
        dataRef.current = data;
        saveDraft(data);
    }, [data]);

    // URL ?ref=&category= rehydrate per v4 source lines 1210-1222 +
    // ORCH-0087 A.3 #4 fallback (no backend lookup).
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const ref = params.get("ref");
            const cat = params.get("category");
            if (
                ref &&
                cat &&
                validateIdempotencyKey(ref) &&
                /^[a-z]+$/.test(cat)
            ) {
                const saved = loadDraft();
                if (saved && saved.firstName) {
                    setData({
                        ...saved,
                        category: cat,
                        kgob_idempotency_key: ref,
                    });
                    setView("confirmation");
                }
            }
        } catch (e) {
            /* silent — bad URL state just starts fresh */
        }
    }, []);

    const next = () => {
        setView((v) => (typeof v === "number" ? v + 1 : v));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const back = () => {
        setView((v) => (typeof v === "number" ? Math.max(0, v - 1) : v));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const performSubmit = useCallback(async (idempotencyKey) => {
        setView("sending");
        setErrorState(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
        const t0 = Date.now();
        try {
            const result = await submitContact(
                { ...dataRef.current, kgob_idempotency_key: idempotencyKey },
                idempotencyKey
            );
            const elapsed = Date.now() - t0;
            const wait = Math.max(0, SENDING_MIN_VISIBLE_MS - elapsed);
            if (wait > 0) await new Promise((r) => setTimeout(r, wait));
            setData((d) => ({
                ...d,
                record_id: result.id,
                kgob_idempotency_key: idempotencyKey,
            }));
            // Push ?ref=&category= into URL via pushConfirmationUrl helper.
            // CHG-0083-M-A2-fix (ORCH-0088 points 2 + 3): encodeURIComponent
            // on both values, defensive omission of `&category=` when absent,
            // warn-and-skip on missing reference id.
            pushConfirmationUrl(idempotencyKey, dataRef.current.category);
            setView("confirmation");
            // CHG-0083-M-A2-fix (ORCH-0088 point 4): KEEP localStorage draft
            // after success so cold-reload of `?ref=` URL still rehydrates
            // Confirmation via the rehydrate-effect at the top of this
            // component. v4 source line 1230-1251 also preserves the draft
            // post-submit. Draft is cleared only by restart() / "Send
            // another message" CTA.
            //   (was: clearDraft() — removed per ORCH-0088 point 4.)
        } catch (e) {
            const elapsed = Date.now() - t0;
            const wait = Math.max(0, SENDING_MIN_VISIBLE_MS - elapsed);
            if (wait > 0) await new Promise((r) => setTimeout(r, wait));
            if (e instanceof SubmitError) {
                setErrorState({
                    kind: e.kind,
                    retryAfter: e.retryAfter || 0,
                });
                setView(e.kind === "validation" ? 4 : "error");
                // Validation errors return to step 4 (last visible step).
                // The 422 path is unexpected in M-A2 because client-side validation
                // should catch everything; if it leaks through, fail-safe to step 4.
            } else {
                setErrorState({ kind: "network", retryAfter: 0 });
                setView("error");
            }
        }
    }, []);

    const submit = useCallback(() => {
        const key =
            dataRef.current.kgob_idempotency_key || generateIdempotencyKey();
        // Stash key into formData early so retry/state restore preserves it.
        setData((d) => ({ ...d, kgob_idempotency_key: key }));
        performSubmit(key);
    }, [performSubmit]);

    const retrySubmit = useCallback(() => {
        const key =
            dataRef.current.kgob_idempotency_key || generateIdempotencyKey();
        performSubmit(key);
    }, [performSubmit]);

    const restart = useCallback(() => {
        clearDraft();
        setData(INITIAL_DATA);
        setErrorState(null);
        // Clear URL params.
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete("ref");
            url.searchParams.delete("category");
            window.history.replaceState({}, "", url.toString());
        } catch (e) {
            /* silent */
        }
        setView(0);
    }, []);

    // ============ Render ============
    if (view === "sending") {
        return (
            <Sending referenceKey={data.kgob_idempotency_key} />
        );
    }
    if (view === "confirmation") {
        return (
            <Confirmation
                data={data}
                referenceKey={data.kgob_idempotency_key}
                onRestart={restart}
            />
        );
    }
    if (view === "error") {
        return (
            <SendingError
                kind={errorState && errorState.kind}
                retryAfter={(errorState && errorState.retryAfter) || 0}
                onRetry={retrySubmit}
                onRestart={restart}
            />
        );
    }

    // Step views (0..4)
    return (
        <>
            {/* CHG-0112 A2 · Honeypot — visually hidden, aria-hidden, not
                tabbable. Real users cannot see, focus, or fill it. Bots
                that iterate all form inputs will. Backend inspects the
                `website` field on POST /api/contact; non-empty triggers
                a silent 202 (no persist, no email). */}
            <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={data.website}
                onChange={(e) =>
                    setData((d) => ({ ...d, website: e.target.value }))
                }
                data-testid="contact-honeypot"
                style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                    opacity: 0,
                }}
            />
            <StepProgress current={view} total={TOTAL_STEPS} />
            {view === 0 && (
                <StepName data={data} setData={setData} onNext={next} />
            )}
            {view === 1 && (
                <StepBusiness
                    data={data}
                    setData={setData}
                    onNext={next}
                    onBack={back}
                />
            )}
            {view === 2 && (
                <StepEmail
                    data={data}
                    setData={setData}
                    onNext={next}
                    onBack={back}
                />
            )}
            {view === 3 && (
                <StepCategory
                    data={data}
                    setData={setData}
                    onNext={next}
                    onBack={back}
                />
            )}
            {view === 4 && (
                <StepDetails
                    data={data}
                    setData={setData}
                    onNext={submit}
                    onBack={back}
                />
            )}
        </>
    );
};

export default MultiStepForm;
